// ============================================================
// Auto-Battle Combat Logic (v2.0)
// ============================================================
import { BASE_ATTACK_INTERVAL, MANA_REGEN_PER_SEC, WIZARD_MANA_REGEN_PER_SEC, SHAMAN_MANA_REGEN_PER_SEC, ASSASSIN_ENERGY_REGEN_PER_SEC, WARRIOR_RAGE_PER_HIT, WARRIOR_RAGE_PER_CRIT, WARRIOR_RAGE_ON_DAMAGED, ARCHER_MAX_ARROWS, ARCHER_RELOAD_TIME, } from '../constants/balance.js';
/**
 * Calculate mitigation (0–1) from a defense stat.
 *
 * Base curve: def / (100 + def)  — standard diminishing returns.
 * Soft cap at 90 %: above that threshold, every extra point of defense
 * yields only 1/10 of the normal mitigation gain, making 95 % very
 * hard and 98 %+ essentially unreachable.
 * Hard cap: 97 %.
 *
 * Reference points:
 *   def  100 →  50 %
 *   def  300 →  75 %
 *   def  900 →  90 %  ← soft-cap kicks in here
 *   def 1800 →  90.5 %
 *   def 9900 →  90.9 %
 *   def ∞    →  ≈ 97 %  (hard cap, never reached in practice)
 */
export function calculateMitigation(defenseStat) {
    const SOFT_CAP = 0.90;
    const HARD_CAP = 0.97;
    const SLOW_DOWN = 0.10; // past soft cap, gains are 10× harder
    const base = defenseStat / (100 + defenseStat);
    if (base <= SOFT_CAP)
        return base;
    const overCap = base - SOFT_CAP;
    return Math.min(HARD_CAP, SOFT_CAP + overCap * SLOW_DOWN);
}
/**
 * Calculate damage dealt from attacker to defender.
 * Uses the soft-capped mitigation curve so high defense values never
 * make a hero unkillable.
 */
export function calculateDamage(attackStat, defenseStat) {
    const mit = calculateMitigation(defenseStat);
    return Math.max(1, Math.round(attackStat * (1 - mit)));
}
/**
 * Check if an attack is a critical hit.
 */
export function rollCrit(critChance) {
    return Math.random() < critChance;
}
/**
 * Get attack interval for a hero in ms.
 * Higher speed = faster attacks.
 */
export function getAttackInterval(speed) {
    return Math.max(200, Math.round(BASE_ATTACK_INTERVAL / speed));
}
/**
 * Compute effective stats for a hero (base + equipment bonuses).
 */
export function getEffectiveStats(hero) {
    const stats = { ...hero.baseStats };
    // Level-up stats are already baked into hero.stats via progression
    stats.hp = hero.stats.hp;
    stats.maxHp = hero.stats.maxHp;
    stats.mp = hero.stats.mp;
    stats.maxMp = hero.stats.maxMp;
    stats.attack = hero.stats.attack;
    stats.defense = hero.stats.defense;
    stats.speed = hero.stats.speed;
    stats.critChance = hero.stats.critChance;
    stats.critDamage = hero.stats.critDamage;
    stats.spellPower = hero.stats.spellPower;
    stats.rage = hero.stats.rage;
    stats.maxRage = hero.stats.maxRage;
    stats.energy = hero.stats.energy;
    stats.maxEnergy = hero.stats.maxEnergy;
    stats.arrowCount = hero.stats.arrowCount;
    stats.maxArrows = hero.stats.maxArrows;
    stats.reloadTimer = hero.stats.reloadTimer;
    // Base resist from hero.stats + transient buff flags (never saved, always zeroed on spawn/death)
    const _amBuff = hero._arcaneMightResistBuff ?? 0;
    stats.fireResist = (hero.stats.fireResist ?? 0) + _amBuff;
    stats.frostResist = (hero.stats.frostResist ?? 0) + _amBuff;
    stats.shadowResist = (hero.stats.shadowResist ?? 0) + _amBuff;
    stats.natureResist = (hero.stats.natureResist ?? 0) + _amBuff;
    // Add equipment bonuses
    for (const slot of Object.keys(hero.equipment)) {
        const item = hero.equipment[slot];
        if (!item)
            continue;
        if (item.statBonuses.hp)
            stats.maxHp += item.statBonuses.hp;
        if (item.statBonuses.mp)
            stats.maxMp += item.statBonuses.mp;
        if (item.statBonuses.attack)
            stats.attack += item.statBonuses.attack;
        if (item.statBonuses.defense)
            stats.defense += item.statBonuses.defense;
        if (item.statBonuses.speed)
            stats.speed += item.statBonuses.speed;
        if (item.statBonuses.critChance)
            stats.critChance += item.statBonuses.critChance;
        if (item.statBonuses.critDamage)
            stats.critDamage += item.statBonuses.critDamage;
        if (item.statBonuses.spellPower)
            stats.spellPower += item.statBonuses.spellPower;
        if (item.statBonuses.fireResist)
            stats.fireResist = (stats.fireResist ?? 0) + item.statBonuses.fireResist;
        if (item.statBonuses.frostResist)
            stats.frostResist = (stats.frostResist ?? 0) + item.statBonuses.frostResist;
        if (item.statBonuses.shadowResist)
            stats.shadowResist = (stats.shadowResist ?? 0) + item.statBonuses.shadowResist;
        if (item.statBonuses.natureResist)
            stats.natureResist = (stats.natureResist ?? 0) + item.statBonuses.natureResist;
        // Armor sub-type stats — sum across equipped items (undefined treated as 0).
        if (item.statBonuses.attackSpeedPct)
            stats.attackSpeedPct = (stats.attackSpeedPct ?? 0) + item.statBonuses.attackSpeedPct;
        if (item.statBonuses.cdrPct)
            stats.cdrPct = (stats.cdrPct ?? 0) + item.statBonuses.cdrPct;
        if (item.statBonuses.blockPct)
            stats.blockPct = (stats.blockPct ?? 0) + item.statBonuses.blockPct;
        if (item.statBonuses.mpRegenPerSec)
            stats.mpRegenPerSec = (stats.mpRegenPerSec ?? 0) + item.statBonuses.mpRegenPerSec;
    }
    return stats;
}
/**
 * Process a hero attacking an enemy.
 * @param distanceToTarget - Optional distance in pixels (used for warrior melee range check).
 * Returns damage dealt and combat events.
 */
export function heroAttackEnemy(hero, enemy, time, distanceToTarget) {
    const stats = getEffectiveStats(hero);
    const events = [];
    // Archer: skip attack if reloading
    if (hero.heroClass === 'archer' && hero.stats.reloadTimer > 0) {
        return { damage: 0, events };
    }
    let damage = calculateDamage(stats.attack, enemy.defense);
    // Warriors (Goomar / Lootfi / Valka) and tanks no longer take the
    // at-range auto-attack penalty (formerly 0.40 / 0.35). With the grid
    // gradient now driving positioning cost, double-dipping the malus
    // through both slot mult AND a "you're not adjacent" mult was making
    // melee placement non-viable. Auto-attacks now deal full damage
    // regardless of strike distance — slot positioning + walk speed are
    // the only positioning levers left.
    if (hero.heroClass === 'warrior') {
        // Rage-cap procs are now perk-gated. Without the perk, full rage just
        // sits there until the player spends it on abilities — no class-wide
        // free Enrage anymore. Three perks tap the same trigger:
        //   - Goomar  → Enrage: 10 s of guaranteed-crit auto-attacks.
        //   - Valka   → Battle Fury: 8 s of attack-speed buff (no auto-crit).
        //   - Lootfi  → has a different perk shape (Forgefire Strikes counts
        //               autos rather than rage-capping), handled in BattleScene.
        const rageFull = hero.stats.rage >= hero.stats.maxRage && hero.stats.maxRage > 0;
        const noProcActive = !hero._enrageActive && !hero._battleFuryActive;
        if (rageFull && noProcActive) {
            const ids = hero.activePassiveIds;
            if (ids?.includes('passive_goomar_enrage')) {
                hero._enrageActive = true;
                hero._enrageTimer = 10000;
                hero.stats.rage = 0;
            }
            else if (ids?.includes('passive_valka_battle_fury')) {
                hero._battleFuryActive = true;
                hero._battleFuryTimer = 8000;
                hero.stats.rage = 0;
            }
        }
    }
    // Enrage: 100 % crit while the perk-driven _enrageActive flag is set
    // (Goomar only — see the rage-trigger block above for gating).
    const isCrit = hero._enrageActive ? true : rollCrit(stats.critChance);
    if (isCrit) {
        damage = Math.round(damage * stats.critDamage);
        events.push({
            type: 'crit',
            sourceId: hero.id,
            targetId: enemy.instanceId,
            damage,
            timestamp: time,
        });
    }
    // Warrior/Dwarf rage gain: 5 per hit, 10 on crit
    if (hero.heroClass === 'warrior') {
        const rageGain = isCrit ? WARRIOR_RAGE_PER_CRIT : WARRIOR_RAGE_PER_HIT;
        hero.stats.rage = Math.min(hero.stats.maxRage, hero.stats.rage + rageGain);
    }
    // Archer: decrement arrow count, trigger reload when empty
    if (hero.heroClass === 'archer') {
        hero.stats.arrowCount -= 1;
        if (hero.stats.arrowCount <= 0) {
            hero.stats.arrowCount = ARCHER_MAX_ARROWS;
            hero.stats.reloadTimer = ARCHER_RELOAD_TIME;
        }
    }
    enemy.currentHp -= damage;
    events.push({
        type: 'hero_attack',
        sourceId: hero.id,
        targetId: enemy.instanceId,
        damage,
        timestamp: time,
    });
    if (enemy.currentHp <= 0) {
        events.push({
            type: 'enemy_death',
            sourceId: hero.id,
            targetId: enemy.instanceId,
            damage: 0,
            timestamp: time,
        });
    }
    return { damage, events };
}
/**
 * Process an enemy attacking a hero.
 */
export function enemyAttackHero(enemy, hero, time) {
    const stats = getEffectiveStats(hero);
    const events = [];
    const damage = calculateDamage(enemy.attack, stats.defense);
    hero.stats.hp -= damage;
    // Warrior/Dwarf gains rage when taking a hit
    if ((hero.heroClass === 'warrior') && hero.stats.maxRage > 0) {
        hero.stats.rage = Math.min(hero.stats.maxRage, hero.stats.rage + WARRIOR_RAGE_ON_DAMAGED);
    }
    events.push({
        type: 'enemy_attack',
        sourceId: enemy.instanceId,
        targetId: hero.id,
        damage,
        timestamp: time,
    });
    if (hero.stats.hp <= 0) {
        hero.stats.hp = 0;
        hero.isDead = true;
        events.push({
            type: 'hero_death',
            sourceId: enemy.instanceId,
            targetId: hero.id,
            damage: 0,
            timestamp: time,
        });
    }
    return { damage, events };
}
/**
 * Regenerate resource for a hero (called every second).
 * Warriors: no regen (rage only from attacks, doesn't decay).
 * Mages: 1 mana/sec.
 * Rogues: 10 energy/sec.
 * Others: 2 mana/sec.
 */
export function regenMana(hero) {
    if (hero.isDead)
        return;
    const stats = getEffectiveStats(hero);
    if (hero.heroClass === 'warrior') {
        // Warriors and dwarves don't regen — rage only gained through combat
        return;
    }
    if (hero.heroClass === 'assassin') {
        // Assassin energy regen
        hero.stats.energy = Math.min(stats.maxEnergy, hero.stats.energy + ASSASSIN_ENERGY_REGEN_PER_SEC);
        return;
    }
    if (hero.heroClass === 'wizard') {
        hero.stats.mp = Math.min(stats.maxMp, hero.stats.mp + WIZARD_MANA_REGEN_PER_SEC);
        return;
    }
    if (hero.heroClass === 'shaman') {
        hero.stats.mp = Math.min(stats.maxMp, hero.stats.mp + SHAMAN_MANA_REGEN_PER_SEC);
        return;
    }
    // All other classes: standard mana regen
    hero.stats.mp = Math.min(stats.maxMp, hero.stats.mp + MANA_REGEN_PER_SEC);
}
//# sourceMappingURL=combat.js.map